import { Program } from '@playbook-technologies/common-enums';
import { Collection } from './collection.domain';
import { isEmpty } from 'lodash';
import { Price } from './domain';
import { z } from 'zod';

const coverError = 'About Tab: Cover is required.';
const programLengthError = 'About Tab: Program Length is required.';
const workoutSchema = z.object({
  workoutId: z.number(),
  name: z.string(),
});
export const readyBaseCollectionSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'About Tab: Title is required.'),
  mediaCover: z
    .object({
      cdn01: z
        .string({ required_error: coverError })
        .min(1, { message: coverError })
        .nullable()
        .refine((value) => value !== null, { message: coverError }),
      image: z
        .string({ required_error: coverError })
        .min(1, { message: coverError })
        .nullable()
        .refine((value) => value !== null, { message: coverError }),
    })
    .nullable()
    .optional()
    .refine((value) => !isEmpty(value), { message: coverError }),
  color: z.string().regex(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, {
    message: 'About Tab: Color must be a valid hex color code.',
  }),
  workouts: z.array(workoutSchema).min(1, {
    message: 'Workouts Tab: At least one workout must be published.',
  }),
});
export const readyProgramSchema = readyBaseCollectionSchema.extend({
  goal: z.string().min(1, 'About Tab: Goal is required.'),
  duration: z
    .tuple([
      z
        .string()
        .regex(/^\d+$/, { message: programLengthError })
        .refine(
          (value) => parseInt(value, 10) >= 1 && parseInt(value, 10) <= 700,
          {
            message: programLengthError,
          },
        ),
      z.string().min(1, { message: programLengthError }),
    ])
    .nullable()
    .refine((value) => value !== null, { message: programLengthError })
    .optional(),
});

export type ReadyCategory = z.infer<typeof readyBaseCollectionSchema>;
export type ReadyProgram = z.infer<typeof readyProgramSchema>;

export const validateToPublish = async (
  collection: Collection,
  price: Price,
) => {
  if (collection.type == Program.Type.Program.value) {
    await readyProgramSchema.parseAsync(collection);
  } else {
    await readyBaseCollectionSchema.parseAsync(collection);
  }
  const mappedErrors = [];
  if (isEmpty(collection.workouts)) {
    mappedErrors.push('Workouts are missing. Add at least one workout');
  }
  if (
    collection.accessType === Program.AccessType.Exclusive.value &&
    !price.price
  ) {
    mappedErrors.push('Price is missing');
  }
  return mappedErrors;
};
